/* eslint-disable quote-props */
import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DeleteOutline, TimerOutlined } from '@mui/icons-material';
import moment from 'moment';
import { usePopupManager } from 'react-popup-manager';
import { SercomTypes } from '../../support/sercom-types';
import SercomDialog from '../../components/SercomDialog';
import SercomConfirmationDialog from '../../components/SercomConfirmationDialog';
import AdminService from '../../services/admin-service';
import snackbarHelper from '../../helpers/snackbar-helper';
import { useTranslation } from 'react-i18next';

type CompanyTimersPopupProps = SercomTypes.SercomDialog & {
  companyId: string;
  companyName: string;
};

const CompanyTimersPopup = (props: CompanyTimersPopupProps) => {
  const [timers, setTimers] = useState<SercomTypes.AdminTimer[] | null>(null);
  const popupManager = usePopupManager();
  const { t } = useTranslation();

  useEffect(() => {
    AdminService.getCompanyTimers(props.companyId).then((result) => {
      setTimers(result.isSuccess ? result.data : []);
    });
  }, [props.companyId]);

  const deleteTimer = async (timer: SercomTypes.AdminTimer) => {
    const result = await AdminService.deleteCompanyTimer(timer.id);
    if (result.isSuccess && result.data?.data) {
      setTimers((current) => current?.filter((item) => item.id !== timer.id) ?? []);
      props.action();
      snackbarHelper.showSuccess('Timer deleted successfully');
    } else {
      snackbarHelper.showErrorFromCode(result.data?.errorCode, t);
    }
  };

  const confirmDelete = (timer: SercomTypes.AdminTimer) => {
    popupManager.open(SercomConfirmationDialog, {
      message: `Delete the timer started ${moment(timer.start).format('lll')}?`,
      action: () => deleteTimer(timer),
      buttonText: t('Delete'),
      onClose: () => {},
    });
  };

  return (
    <SercomDialog title={`Running timers · ${props.companyName}`} isOpen={props.isOpen} onClose={props.onClose} maxWidth={false} paperWidth="800px" closeButtonText={t('Close')} action={() => {}}>
      <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
        <Alert severity="warning" sx={{ mb: 2.5, py: .25, border: '1px solid #F3D184', bgcolor: '#FFF8E1', color: '#765300', '& .MuiAlert-icon': { color: '#C88700' }, '& .MuiAlert-message': { fontSize: 13, fontWeight: 650 } }}>
          Only timers older than 24 hours can be deleted.
        </Alert>
        {timers === null ? <Box sx={{ height: 160, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box> : timers.length === 0 ? <Box sx={{ height: 160, display: 'grid', placeItems: 'center', color: '#71809C' }}><Stack alignItems="center" spacing={1}><TimerOutlined /><Typography>No running timers</Typography></Stack></Box> : <Box sx={{ border: '1px solid #E8ECF4', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr .85fr 1.1fr 36px', gap: 1, px: 2, py: 1.25, bgcolor: '#fff', borderBottom: '1px solid #E8ECF4', color: '#71809C', fontSize: 10, fontWeight: 750, letterSpacing: '.4px' }}><span>MEMBER</span><span>ACTIVITY</span><span>SOURCE</span><span>STARTED</span><span /></Box>
          {timers.map((timer) => <Box key={timer.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr .85fr 1.1fr 36px', gap: 1, alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #EEF1F6', '&:last-child': { borderBottom: 0 } }}><Typography noWrap sx={{ color: '#111', fontSize: 13, fontWeight: 650 }}>{timer.memberName}</Typography><Typography noWrap sx={{ color: '#111', fontSize: 13 }}>{timer.activityName}</Typography><Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ color: '#111', fontSize: 13 }}>{timer.sourceCode || '—'}</Typography>{timer.clientName && <Typography noWrap sx={{ color: '#71809C', fontSize: 11, mt: .2 }}>{timer.clientName}</Typography>}</Box><Box><Typography noWrap sx={{ color: '#111', fontSize: 13 }}>{moment(timer.start).format('lll')}</Typography><Typography sx={{ color: '#71809C', fontSize: 11, mt: .2 }}>{moment(timer.start).fromNow()}</Typography></Box><Tooltip title={timer.canDelete ? 'Delete timer' : 'Timers can be deleted after 24 hours'}><span><IconButton disabled={!timer.canDelete} onClick={() => confirmDelete(timer)} size="small" sx={{ color: '#C85C55', '&.Mui-disabled': { color: '#CBD3E0' } }}><DeleteOutline fontSize="small" /></IconButton></span></Tooltip></Box>)}
        </Box>}
      </Box>
    </SercomDialog>
  );
};

export default CompanyTimersPopup;
